/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import { useState, useEffect } from "react";
import Navbar from "./Navbar";
import Sidebar from "./Sidebar";
import fondo from '../../assets/zamora.jpg';
import { useMsal } from "@azure/msal-react";
import jwtDecode from "jwt-decode";


import MiPerfil from "../user/MiPerfil";
import PersonasTable from "../personas/PersonasTable";
import DepartamentosTable from "../departamentos/DepartamentosTable";
import EdificiosTable from "../edificios/EdificiosTable";

import useVersionMovil from "../../hooks/useMobileVersion";
import useVersionTablet from "../../hooks/useTabletVersion";

import { Offcanvas} from "react-bootstrap";
import { Button } from "react-bootstrap";

/**
 * Componente principal de layout de la aplicación.
 * 
 * Gestiona la navegación entre secciones y la autenticación del usuario.
 * 
 */
const MainLayout = ({ children }) => {
  /* ============================
     Estado y autenticación
     ============================ */

  // Sección activa actual (busqueda, departamentos, edificios, ayuda)
  const [activeSection, setActiveSection] = useState("busqueda");
  
  // Roles del usuario obtenidos del token JWT
  const [roles, setRoles] = useState([]);

  // Instancia de MSAL para autenticación
  const { instance, accounts } = useMsal();

  /* ============================
     Responsividad
     ============================ */
  // Detectar tipo de dispositivo mediante hooks personalizados
  const isTablet = useVersionTablet();
  const isMobile = useVersionMovil();

  // Controlar visibilidad del menú Offcanvas en móvil/tablet
  const [showMenu, setShowMenu] = useState(false);

  /* ============================
     Efectos
     ============================ */

  /**
   * Obtiene y decodifica los roles del usuario desde el token JWT.
   * Se ejecuta cuando cambian las cuentas de MSAL.
   */
  useEffect(() => {
    const loadRoles = async () => {
      if (!accounts || accounts.length === 0) return;
      try {
         // Obtener token de acceso
        const response = await instance.acquireTokenSilent({
          account: accounts[0],
          scopes: [process.env.REACT_APP_API_SCOPE],
        });
        // Decodificar token para extraer roles
        const decoded = jwtDecode(response.accessToken);
        setRoles(decoded.roles || []);
      } catch (err) {
        console.error("Error obteniendo roles en MainLayout:", err);
        setRoles([]);
      }
    };
    loadRoles();
  }, [accounts, instance]);

  /**
   * Cierra automáticamente el menú Offcanvas al cambiar a versión escritorio.
   */
  useEffect(() => {
    if (!isMobile) setShowMenu(false);
  }, [isMobile]);

  /* ============================
      Lógica de permisos
     ============================ */
  
  // Determinar si el usuario tiene permisos de administrador
  const isAdmin = roles.includes("ROLE_ADMIN");

  /*************************************************************
   * Renderizado del layout principal                          *
   *************************************************************/
  return (
    <div>
      {/* =======================================
           Fondo difuminado con imagen de Zamora
          ======================================= */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundImage: `url(${fondo})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "blur(5px)",
          opacity: 0.5,
          zIndex: 0,
        }}
      />
      {/* =============================
          Barra de navegación superior
          ============================= */}
      <Navbar isMobile={isMobile} isTablet={isTablet} onMenuClick={() => setShowMenu(true)}/>
      
      {/* ================================================
           Menú desplegable Offcanvas (solo móvil/tablet)
          ================================================ */}
      {(isMobile || isTablet) && (
        <Offcanvas
          show={showMenu}
          onHide={() => setShowMenu(false)}
          placement="start"
          className="bg-dark text-white"
        >
          <Offcanvas.Header closeButton closeVariant="white">
            <Offcanvas.Title>
              <i className="bi bi-grid me-2" />
              Directorio
            </Offcanvas.Title>
          </Offcanvas.Header>

          <Offcanvas.Body className="p-0 d-flex flex-column">
            {/* Sidebar dentro del Offcanvas */}
            <Sidebar
              activeSection={activeSection}
              setActiveSection={(section) => {
                setActiveSection(section);
                setShowMenu(false);
              }}
              isAdmin={isAdmin}
              isMobile={isMobile}
              isTablet={isTablet}
            />
          </Offcanvas.Body>
        </Offcanvas>
      )}

      {/* ========================================================================================
                                            Contenido principal
          ======================================================================================== */}
      
       {/* ================================Versión Móvil ================================*/}
      {isMobile && !isTablet && (
        <div className="container mt-3">
          {activeSection === "busqueda" && <PersonasTable />}
          {activeSection === "departamentos" && <DepartamentosTable />}
          {activeSection === "edificios" && <EdificiosTable />}
	  {activeSection === "miPerfil" && <MiPerfil />}
          {activeSection === "ayuda" && (
            <div className="card shador m-2">
              <div className="card-body p-3">
                <h2 className="card-title mb-3">Ayuda y guía de uso</h2>
                <hr />
                <p>Bienvenido al Directorio de Personal del Ayuntamiento de Zamora.</p>
                <ul>
                  <li>Usa el menú lateral para navegar entre secciones.</li>
                  <li>Consulta los datos de personas, departamentos y edificios.</li>
                </ul>
		<hr />
                <p>Si no estás registrado, rellena el siguiente formulario:</p>    
                <Button
                   id="btnModificar"
                   onClick={() => window.location.href = "https://forms.office.com/Pages/ResponsePage.aspx?id=FPvpGfE_gEmdVX3x2-EGmgtKDhBvwgBAmHUjhyE5aOVUNExMOEMxWFdTMlVSVkw3R1dXMENNSUFaMC4u"}
                >
                   Registra tus datos
                </Button>
                <hr />
                <p>Si tus datos no son correctos, puedes modificarlos aquí:</p>    
                <button
                  className="btn btn-primary" 
               	  onClick={() => setActiveSection("miPerfil")}
               	>
               	  Solicitar cambio de datos
               	</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ================================Versión Tablet ================================*/}
      {isTablet && !isMobile && (
        <div className="container mt-3">
          <div className="row justify-content-center">
            <div className="card shadow" style={{ minHeight: "75vh", flexDirection: "row", flexWrap: "wrap" }}>
              <div className="card-body">
                <div className="row" style={{ minHeight: "70vh" }}>
                  <div className={"col-12"}>
                    {activeSection === "busqueda" && <PersonasTable />}
                    {activeSection === "departamentos" && <DepartamentosTable />}
                    {activeSection === "edificios" && <EdificiosTable />}
		    {activeSection === "miPerfil" && <MiPerfil />}
                    {activeSection === "ayuda" && (
                      <div className="card shador m-2">
                        <div className="card-body p-3">
                          <h2 className="card-title mb-3">Ayuda y guía de uso</h2>
                          <hr />
                          <p>Bienvenido al Directorio de Personal del Ayuntamiento de Zamora.</p>
                          <ul>
                            <li>Usa el menú lateral para navegar entre secciones.</li>
                            <li>Consulta los datos de personas, departamentos y edificios.</li>                            
                          </ul>
			  <hr />
                	  <p>Si no estás registrado, rellena el siguiente formulario:</p>    
                	  <Button
                	     id="btnModificar"
                	     onClick={() => window.location.href = "https://forms.office.com/Pages/ResponsePage.aspx?id=FPvpGfE_gEmdVX3x2-EGmgtKDhBvwgBAmHUjhyE5aOVUNExMOEMxWFdTMlVSVkw3R1dXMENNSUFaMC4u"}
                	  >
                	     Registra tus datos
                	  </Button>
                          <hr />
                          <p>Si tus datos no son correctos, puedes modificarlos aquí:</p>    
                          <button
                  	    className="btn btn-primary" 
                  	    onClick={() => setActiveSection("miPerfil")}
                	  >
                  	    Solicitar cambio de datos
                	  </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================================Versión Ordenador ================================*/}
      {!isMobile && !isTablet && (
      <div className="container mt-3">
        <div className="row justify-content-center">
          <div className="card shadow" style={{ minHeight: "75vh", flexDirection: "row", flexWrap: "wrap" }}>
            <div className="card-body">
              <div className="row" style={{ minHeight: "70vh"}}>
                {/* Sidebar */}
                <Sidebar activeSection={activeSection} setActiveSection={setActiveSection} user={null} isAdmin={isAdmin}/>
                
                {/* Panel derecho */}
                <div className={isMobile ? "col-12" : "col-md-9"}>
                  {activeSection === "busqueda" && <PersonasTable />}
                  {activeSection === "departamentos" && <DepartamentosTable />}
                  {activeSection === "edificios" && <EdificiosTable />}
		  {activeSection === "miPerfil" && <MiPerfil />}
                  {activeSection === "ayuda" && (
                    <div className="card shador m-2">
                      <div className="card-body p-3">
                        <h2 className="card-title mb-3">Ayuda y guía de uso</h2>
                        <hr />
                        <p>Bienvenido al Directorio de Personal del Ayuntamiento de Zamora.</p>
                        <ul>
                          <li>Usa el menú lateral para navegar entre secciones.</li>
                          <li>Consulta los datos de personas, departamentos y edificios.</li>                      
                        </ul>
			<hr />
                	<p>Si no estás registrado, rellena el siguiente formulario:</p>    
                	<Button
                	   id="btnModificar"
                	   onClick={() => window.location.href = "https://forms.office.com/Pages/ResponsePage.aspx?id=FPvpGfE_gEmdVX3x2-EGmgtKDhBvwgBAmHUjhyE5aOVUNExMOEMxWFdTMlVSVkw3R1dXMENNSUFaMC4u"}
                	>
                	   Registra tus datos
                	</Button>
                        <hr />
                        <p>Si tus datos no son correctos, puedes modificarlos aquí:</p>    
                        <button
                  	  className="btn btn-primary" 
                  	  onClick={() => setActiveSection("miPerfil")}
                	>
                  	  Solicitar cambio de datos
                	</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default MainLayout;
